/* Selection is local to this browser session. All specification values come from Liquid. */
(() => {
  const initialized = Symbol.for('ttscooter.scooterComparison');
  if (window[initialized]) return;
  window[initialized] = true;
  const storageKey = 'ttscooter:scooter-compare:v1';
  const selectionParam = 'sc_compare';
  const instances = new WeakMap();

  class ScooterComparison {
    constructor(root) {
      this.root = root;
      this.controller = new AbortController();
      this.choices = [...root.querySelectorAll('[data-sc-compare-choice]')];
      this.models = new Map(this.choices.map(input => [input.value, input.dataset.modelName]));
      this.selected = [];
      this.differences = false;
      this.table = root.querySelector('.sc-comparison-table');
      this.scroll = root.querySelector('.sc-comparison-scroll');
      this.hint = root.querySelector('.sc-comparison-scroll-hint');
      this.state = root.querySelector('[data-sc-compare-state]');
      this.go = root.querySelector('[data-sc-compare-go]');
      this.tray = root.querySelector('.sc-compare-selection--fixed');
      if (!this.models.size) return;

      // A repeated editor block must not create duplicate comparison columns.
      root.querySelectorAll('tr').forEach(row => {
        const seen = new Set();
        row.querySelectorAll('[data-sc-compare-column]').forEach(cell => {
          const id = cell.dataset.scCompareColumn;
          cell.dataset.scDuplicate = String(seen.has(id));
          seen.add(id);
        });
      });
      const seenChoices = new Set();
      this.choices.forEach(input => {
        if (seenChoices.has(input.value)) {
          const duplicate = input.closest('.sc-compare-option') || input.closest('.sc-product-card')?.parentElement;
          if (duplicate) duplicate.hidden = true;
        }
        seenChoices.add(input.value);
      });

      const params = new URL(window.location.href).searchParams;
      let saved = [];
      try { saved = JSON.parse(sessionStorage.getItem(storageKey) || '[]'); } catch { /* Storage may be blocked. */ }
      this.setSelection(params.has(selectionParam) ? params.get(selectionParam).split(',') : saved);
      this.differences = params.get('sc_diff') === '1';
      root.classList.add('sc-comparison--interactive');
      root.querySelectorAll('[data-sc-choice-ui]').forEach(element => { element.hidden = false; });
      root.addEventListener('change', event => this.onChange(event), { signal: this.controller.signal });
      root.addEventListener('click', event => this.onClick(event), { signal: this.controller.signal });
      window.addEventListener('popstate', () => {
        const url = new URL(window.location.href);
        this.setSelection((url.searchParams.get(selectionParam) || '').split(','));
        this.differences = url.searchParams.get('sc_diff') === '1';
        this.render();
      }, { signal: this.controller.signal });
      this.render();
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => this.updateLayout());
        [this.scroll, this.table, this.tray].filter(Boolean).forEach(element => this.observer.observe(element));
      } else {
        window.addEventListener('resize', () => this.updateLayout(), { passive: true, signal: this.controller.signal });
      }
    }

    setSelection(ids) {
      this.selected = [...new Set(Array.isArray(ids) ? ids : [])].filter(id => this.models.has(id)).slice(0, 3);
    }

    persist() {
      try { sessionStorage.setItem(storageKey, JSON.stringify(this.selected)); } catch { /* URL state still works. */ }
      const url = new URL(window.location.href);
      url.searchParams.set(selectionParam, this.selected.join(','));
      if (this.differences) url.searchParams.set('sc_diff', '1');
      else url.searchParams.delete('sc_diff');
      try { window.history.replaceState(window.history.state, '', url); } catch { /* Preview frames may restrict history. */ }
    }

    onChange(event) {
      const input = event.target;
      if (input.matches('[data-sc-compare-choice]')) {
        this.setSelection(input.checked ? [...this.selected, input.value] : this.selected.filter(id => id !== input.value));
      } else if (input.matches('[data-sc-compare-differences]')) {
        this.differences = input.checked && this.selected.length >= 2;
      } else return;
      this.persist();
      this.render();
    }

    onClick(event) {
      const go = event.target.closest('[data-sc-compare-go]');
      if (go && go.getAttribute('aria-disabled') === 'true') event.preventDefault();
      const remove = event.target.closest('[data-sc-compare-remove]');
      if (remove) {
        const id = remove.dataset.scCompareRemove;
        this.selected = this.selected.filter(value => value !== id);
        this.persist();
        this.render();
        this.choices.find(input => input.value === id)?.focus({ preventScroll: true });
      }
      if (event.target.closest('[data-sc-compare-clear]')) {
        this.selected = [];
        this.differences = false;
        this.persist();
        this.render();
        this.choices[0]?.focus({ preventScroll: true });
      }
    }

    compareUrl() {
      if (!this.root.dataset.compareUrl) return null;
      const current = new URL(window.location.href);
      const destination = new URL(this.root.dataset.compareUrl, current);
      if (destination.origin !== current.origin) return null;
      // The destination controls its own ?view. Retain Shopify's preview parameters.
      const keys = new Set(current.searchParams.keys());
      keys.forEach(key => {
        if (['view', selectionParam, 'sc_diff'].includes(key) || destination.searchParams.has(key)) return;
        current.searchParams.getAll(key).forEach(value => destination.searchParams.append(key, value));
      });
      destination.searchParams.set(selectionParam, this.selected.join(','));
      return destination.href;
    }

    render() {
      const count = this.selected.length;
      this.choices.forEach(input => {
        input.checked = this.selected.includes(input.value);
        input.disabled = count >= 3 && !input.checked;
        input.closest('label').dataset.selected = String(input.checked);
        input.closest('label').dataset.unavailable = String(input.disabled);
      });
      const status = this.root.querySelector('[data-sc-compare-status]');
      status.textContent = `${count} of 3 selected. ${count < 2 ? 'Choose at least 2.' : count === 3 ? 'Remove one to choose another.' : 'You can add one more.'}`;
      this.root.querySelector('[data-sc-compare-clear]').disabled = count === 0;
      const chips = this.root.querySelector('[data-sc-compare-chips]');
      chips.replaceChildren(...this.selected.map(id => {
        const item = document.createElement('li');
        item.className = 'sc-compare-chip';
        const name = document.createElement('span');
        name.className = 'sc-compare-chip-name';
        name.textContent = this.models.get(id);
        name.title = this.models.get(id);
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.scCompareRemove = id;
        button.setAttribute('aria-label', `Remove ${this.models.get(id)} from comparison`);
        button.textContent = '×';
        item.append(name, button);
        return item;
      }));
      if (this.go) {
        const url = this.compareUrl();
        const canGo = count >= 2 && Boolean(url);
        this.go.setAttribute('aria-disabled', String(!canGo));
        this.go.tabIndex = canGo ? 0 : -1;
        if (canGo) this.go.href = url;
        else this.go.removeAttribute('href');
        if (!url) status.textContent += ' The comparison page is not available yet.';
      }
      const differenceControl = this.root.querySelector('[data-sc-compare-differences]');
      if (differenceControl) {
        differenceControl.disabled = count < 2;
        differenceControl.checked = this.differences;
      }
      if (this.table) this.renderTable();
      this.updateLayout();
    }

    renderTable() {
      const count = this.selected.length;
      let suppliedValues = 0;
      let visibleRows = 0;
      let confirmedDifferences = 0;
      let incompleteRows = 0;
      this.table.style.setProperty('--sc-comparison-columns', String(Math.max(2, count)));
      this.table.querySelectorAll('[data-sc-compare-column]').forEach(cell => {
        cell.hidden = !this.selected.includes(cell.dataset.scCompareColumn) || cell.dataset.scDuplicate === 'true';
      });
      this.table.querySelectorAll('[data-sc-compare-row]').forEach(row => {
        const cells = [...row.querySelectorAll('[data-sc-compare-column]')].filter(cell => !cell.hidden);
        const values = cells.filter(cell => cell.dataset.scSpecKnown === 'true').map(cell => cell.dataset.scSpecValue.trim().replace(/\s+/g, ' ').toLowerCase());
        const uniqueValues = new Set(values);
        const completeMatch = values.length === count && count >= 2 && uniqueValues.size === 1;
        suppliedValues += values.length;
        if (uniqueValues.size > 1) confirmedDifferences += 1;
        if (values.length < count) incompleteRows += 1;
        // Only known, equal values are removed. Missing values never prove a match.
        row.hidden = this.differences && completeMatch;
        if (!row.hidden) visibleRows += 1;
      });
      let message = '';
      let showTable = count >= 2;
      if (count < 2) {
        message = 'Choose at least 2 scooters above to compare. You can select up to 3.';
      } else if (suppliedValues === 0) {
        message = 'Specifications have not been supplied for these models. Differences cannot be determined yet.';
        if (this.differences) showTable = false;
      } else if (this.differences && visibleRows === 0) {
        message = 'All supplied specifications match. Turn off “Only show differences” to view the full table.';
        showTable = false;
      } else if (this.differences && incompleteRows > 0) {
        message = confirmedDifferences === 0
          ? 'No differences can be confirmed from the supplied specifications. Rows with missing values remain visible.'
          : 'Rows with missing specifications remain visible; missing values are not confirmed differences.';
      }
      this.scroll.hidden = !showTable;
      this.state.textContent = message;
      this.state.hidden = !message;
    }

    updateLayout() {
      if (this.scroll) {
        const canScroll = !this.scroll.hidden && this.scroll.scrollWidth > this.scroll.clientWidth + 1;
        this.hint.hidden = !canScroll;
        this.scroll.tabIndex = canScroll ? 0 : -1;
      }
      if (this.tray) this.root.style.setProperty('--sc-tray-height', `${this.tray.getBoundingClientRect().height}px`);
    }

    disconnect() {
      this.controller.abort();
      this.observer?.disconnect();
    }
  }

  const initialize = scope => {
    const roots = [...scope.querySelectorAll('[data-sc-comparison]')];
    if (scope.matches?.('[data-sc-comparison]')) roots.unshift(scope);
    roots.forEach(root => {
      if (!instances.has(root)) instances.set(root, new ScooterComparison(root));
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  else initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-sc-comparison]').forEach(root => {
      instances.get(root)?.disconnect();
      instances.delete(root);
    });
  });
})();
